import { CloseSharp, SearchSharp } from '@mui/icons-material';
import { Popper, Fade, Paper, IconButton, Stack, Typography, FormGroup, FormControlLabel, Radio, TextField, InputAdornment, Grid } from '@mui/material';
import { StandardPagination } from '@shared/components/StandardPagination';
import { colorTheme } from '@styles/muiTheme';
import { LenderOptionsOuterGrid, LenderOptionTypography } from '@styles/Tools/LenderControl/LenderControlsHeaderStyles';
import { ILenderListItem } from './LenderControlsHeader';

interface ILenderSelectorPopperProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    anchorEl: HTMLElement | null;
    popperDivRef: React.RefObject<HTMLDivElement>;
    numColumns: number;
    columns: ILenderListItem[][];
    pageNumber: number;
    pageCount: number;
    handleSelectLender: (lender: ILenderListItem) => void;
    handleActiveLendersFilter: () => void;
    activeLendersFilter: boolean;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangePage: (event: React.ChangeEvent<unknown>, value: number) => void;
}

export const LenderSelectorPopper = ({
    open,
    setOpen,
    anchorEl,
    popperDivRef,
    numColumns,
    columns,
    pageNumber,
    pageCount,
    handleSelectLender,
    handleActiveLendersFilter,
    activeLendersFilter,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    handleChangePage
}: ILenderSelectorPopperProps) => {
    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? 'transition-popper' : undefined;

    return (
        <Popper ref={popperDivRef as React.RefObject<HTMLDivElement>} transition id={id} open={open} anchorEl={anchorEl} placement='bottom-start' sx={{ zIndex: 99 }}>
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                    <Paper elevation={12} sx={{ p: 2, minWidth: '73vw', borderRadius: '12px' }}>
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                            }}
                            onClick={() => setOpen(!open)}
                        >
                            <CloseSharp />
                        </IconButton>
                        <Stack 
                            direction="row"
                            spacing={2}
                            sx={{
                                justifyContent: 'space-evenly',
                                alignItems: 'center',
                                alignContent: 'center',
                                mb: 2
                            }}
                        >
                            <Typography variant="h4" fontWeight={600}>Lender List</Typography>
                            <FormGroup>
                                <Stack 
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        justifyContent: 'space-evenly',
                                        alignItems: 'center',
                                        alignContent: 'center'
                                    }}
                                >
                                    <FormControlLabel
                                        label="Active Lenders"
                                        control={
                                            <Radio
                                                checked={activeLendersFilter}
                                                color="primary"
                                                onChange={handleActiveLendersFilter}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="All Lender List"
                                        control={
                                            <Radio
                                                checked={!activeLendersFilter}
                                                color="primary"
                                                onChange={handleActiveLendersFilter}
                                            />
                                        }
                                    />
                                </Stack>
                            </FormGroup>
                            <TextField
                                fullWidth
                                label="Search"
                                variant="outlined"
                                value={searchTerm}
                                sx={{ mb: 1, width: '50%' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchSharp />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="clear"
                                                edge="end"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                }}
                                            >
                                                <CloseSharp />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={handleSearchChange}
                            />
                        </Stack>
              <LenderOptionsOuterGrid numColumns={numColumns}>
                {columns.map((column, columnIndex) => (
                  <Grid key={columnIndex} item xs={12}>
                    {column.map(option => (
                      <Grid key={option.id} item xs={12} rowSpacing={4} onClick={() => handleSelectLender(option)}>
                        <LenderOptionTypography key={option.name} option={option}>
                          {option.name}
                        </LenderOptionTypography>
                      </Grid>
                    ))}
                  </Grid>
                ))}
              </LenderOptionsOuterGrid>
              <StandardPagination
                paginationIconBgColor={colorTheme.palette.primary.light}
                paginationIconColor={colorTheme.palette.common.white}
                page={pageNumber}
                pageCount={pageCount}
                onChange={handleChangePage}
              />
            </Paper>
          </Fade>
        )}
    </Popper>
);};